import { useState, useEffect } from 'react';
import type { WidgetDefinition, UserWidgetPreference, DashboardPeriod, DashboardFetcher } from '../types';
import { getCatalogWidgets } from '../api/dashboardCatalog';
import { getUserLayout } from '../api/dashboardLayout';
import { resolveWidgetPermissions } from '../ability';

interface UseWidgetEngineProps {
  userId: string;
  userRoles: string[];
  fetcher: DashboardFetcher;
  /**
   * Si el usuario administra el catalogo. **Solo se usa como respaldo** cuando el backend no manda
   * `permissions` en el DTO del widget. Ver `resolveWidgetPermissions`.
   */
  isAdmin?: boolean;
}

/**
 * Motor central de orquestación.
 * Obtiene el Catálogo de Widgets disponibles para el usuario y el Layout guardado.
 * Gestiona el estado global de la vista (periodo de fecha actual y modo de edición).
 */
export const useWidgetEngine = ({ userId, userRoles, fetcher, isAdmin = false }: UseWidgetEngineProps) => {
  const [catalog, setCatalog] = useState<WidgetDefinition[]>([]);
  const [initialLayout, setInitialLayout] = useState<UserWidgetPreference[]>([]);
  
  // Estado de la UI
  const [isLoading, setIsLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [period, setPeriod] = useState<DashboardPeriod>('Mes');

  // Inicialización (Traer catálogo y preferencias)
  useEffect(() => {
    let isMounted = true;

    const initEngine = async () => {
      setIsLoading(true);
      try {
        // Ejecutamos ambas peticiones en paralelo
        const [catalogData, layoutData] = await Promise.all([
          getCatalogWidgets(fetcher),
          getUserLayout(userId, fetcher)
        ]);

        if (isMounted) {
          /**
           * El catalogo llega YA filtrado por el servidor cuando el host evalua acceso por
           * instancia, y entonces `can_view` es siempre `true` en lo que llega: este filtro no
           * quita nada y esta bien que no quite nada. Lo que importa es que ya **no** se aplica el
           * OR de roles encima, que era lo que escondia un widget compartido con alguien que no
           * tiene su modulo. Sin `permissions`, el respaldo reproduce el filtro historico.
           */
          setCatalog(
            catalogData.filter(w => resolveWidgetPermissions(w, { userRoles, isAdmin }).can_view),
          );
          setInitialLayout(layoutData);
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Error desconocido al cargar el dashboard';
        console.error('[KPI Dashboard] Error al inicializar el motor de widgets:', e);
        if (isMounted) setInitError(message);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initEngine();

    return () => {
      isMounted = false;
    };
  }, [userId, fetcher]);

  const toggleEditMode = () => setIsEditMode(prev => !prev);
  
  const handlePeriodChange = (newPeriod: DashboardPeriod) => {
    setPeriod(newPeriod);
    // Cambiar este estado forzará un re-render en cascada a los componentes,
    // inyectando el nuevo 'period' en sus props y forzando a TanStack Query a buscar nuevos datos.
  };

  /**
   * Helper para obtener la definición completa de un widget dado su ID
   */
  const getWidgetDefinition = (widgetId: string): WidgetDefinition | undefined => {
    return catalog.find(w => w.widget_id === widgetId);
  };

  /**
   * Agrega un widget recién creado (via Builder) al catálogo local
   * sin necesidad de refetch completo desde el backend.
   */
  const addToCatalog = (widget: WidgetDefinition) => {
    setCatalog(prev => [...prev, widget]);
  };

  /**
   * Actualiza un widget existente en el catálogo local (tras edición via Builder).
   */
  const updateInCatalog = (updated: WidgetDefinition) => {
    setCatalog(prev => prev.map(w => w.widget_id === updated.widget_id ? updated : w));
  };

  /**
   * Elimina un widget del catálogo local (tras DELETE en backend).
   */
  const removeFromCatalog = (widgetId: string) => {
    setCatalog(prev => prev.filter(w => w.widget_id !== widgetId));
  };

  return {
    catalog,
    initialLayout,
    isLoading,
    initError,
    // UI State
    isEditMode,
    toggleEditMode,
    period,
    setPeriod: handlePeriodChange,
    // Helpers
    getWidgetDefinition,
    addToCatalog,
    updateInCatalog,
    removeFromCatalog,
  };
};
